"use client";

import * as React from "react";
import Image from "next/image";
import { ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { ASSET_PREFIX } from "../../../config";
import Link from "next/link";
import VideoPopupGallery from "./UnitedSpacePopupVideo";

export type AccordionItem = {
  id: string | number;
  title: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
};

export type AccordionProps = {
  items: AccordionItem[];
  single?: boolean;
  defaultOpenIds?: Array<AccordionItem["id"]>;
  className?: string;
  itemClassName?: string;
};

export const Accordion: React.FC<AccordionProps> = ({
  items,
  single = false,
  defaultOpenIds = [],
  className = "",
  itemClassName = "",
}) => {
  const [open, setOpen] = React.useState<Set<AccordionItem["id"]>>(
    () => new Set(defaultOpenIds)
  );

  const toggle = (id: AccordionItem["id"], disabled?: boolean) => {
    if (disabled) return;

    setOpen((prev) => {
      const next = new Set(prev);
      const isOpen = next.has(id);

      if (single) {
        next.clear();
        if (!isOpen) next.add(id);
      } else {
        isOpen ? next.delete(id) : next.add(id);
      }

      return next;
    });
  };
  

  return (
    <div
      className={
        "w-full divide-y divide-[#b08d38] border-b border-[#b08d38] bg-white " +
        className
      }
    >
      {items.map(({ id, title, content, disabled }, idx) => {
        const isOpen = open.has(id);
        const panelId = `accordion-panel-${id}`;
        const headingId = `accordion-heading-${id}`;

        return (
          <div
            key={id}
            className={
              "group/acc pr-4 sm:pr-6 " +
              (idx === 0 ? "rounded-t-2xl " : "") +
              (idx === items.length - 1 ? "rounded-b-2xl " : "") +
              itemClassName
            }
          >
            <button
              id={headingId}
              className={
                "flex w-full items-center justify-between gap-4 py-4 text-left outline-none transition-opacity cursor-pointer " +
                (disabled
                  ? "cursor-not-allowed opacity-50"
                  : "hover:opacity-90")
              }
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => toggle(id, disabled)}
              disabled={disabled}
            >
              <h2 className="text-[20px] leading-[28px] 2xl:text-[20px] 2xl:leading-[28px] font-medium text-gray-900">
                {title}
              </h2>

              <div className="p-[10px] rounded-[50%] bg-[#b1b1b1] w-[40px] h-[40px] flex items-center justify-center transition-transform duration-300">
                <ChevronDown
                  className={
                    "h-6 w-6 shrink-0 text-white transition-transform duration-300 text-[#000] " +
                    (isOpen ? "rotate-180" : "rotate-0")
                  }
                  aria-hidden="true"
                />
              </div>
            </button>

            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.section
                  key="content"
                  id={panelId}
                  role="region"
                  aria-labelledby={headingId}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{
                    duration: 0.24,
                    ease: [0.2, 0.8, 0.2, 1],
                  }}
                  style={{ overflow: "hidden" }}
                >
                  <div className="pb-5 text-[16px] leading-[22px] sm:text-base leading-relaxed w-[100%] md:w-[90%] text-gray-700">
                    {content}
                  </div>
                </motion.section>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};

export const AccordionDemo: React.FC = () => {
  const items: AccordionItem[] = [
    {
      id: 1,
      title: "Where is UNITD OS-49 located?",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            UNITD OS-49 is planned at Chinnavedampatti, in the Saravanampatti
            area of Coimbatore - the same corridor where MUH 49.2 and MUH - U49
            are located.
          </p>
        </>
      ),
    },
    {
      id: 2,
      title: "What is UNITD OS?",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            UNITD OS - Office Space is UNITD Living Space&apos;s new commercial
            workspace platform. It&apos;s designed as flexible,
            technology-enabled office space for IT companies, startups, SMEs,
            consultants, and larger enterprises, and includes a dedicated
            incubation centre to support early-stage businesses.
          </p>
        </>
      ),
    },
    {
      id: 3,
      title: "How big is the UNITD OS-49 project?",
      content: (
        <>
          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            The development is planned at approximately 2.2 lakh sq. ft. of
            commercial space, backed by a ₹128 crore investment, and is expected
            to create more than 2000+ workspaces.
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="">
      <Accordion items={items} single defaultOpenIds={[1]} />
    </div>
  );
};

export default function UnitdSpace128Crore() {
    
  const data = [
    {
      image:
        "/images/blogs/unitd-living-space-signs-128-crore-mou-for-a-new-it-project-in-saravanampatti.webp",

      title:
        "UNITD Living Space Signs ₹128 Crore MoU for a New IT Project in Saravanampatti - What It Means for MUH",

      content1: (
        <>
          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            UNITD Living Space has signed a Memorandum of Understanding with the Government of Tamil Nadu for a new commercial development in Chinnavedampatti, Saravanampatti, Coimbatore - 641 049 - the same growth corridor where MUH 49.2 is already being built. The investment: ₹128 crore. The expected outcome: 2000+ Workplace created under the name <strong>UNITD OS - Office Space</strong>.
          </p>


          {/* popup video  */}
          <VideoPopupGallery />


          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            If you&apos;re someone who’s choosing Saravanampatti as a place to live, work, or invest, here&apos;s the number that matters most: your neighbourhood just got a confirmed IT-infrastructure upgrade by UNITD Living Spaces.
          </p>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            Below, we break down what&apos;s actually being built and what it means for Coimbatore&apos;s Corporate infrastructure.
          </p>

          <h2 className=" text-gray-900  my-[20px] text-[20px] leading-[28px] font-[400] max-xxl:text-[18px] max-xxl:leading-[26px]  max-mlg:text-[16px] max-mlg:leading-[24px] max-xl:my-[15px]">
            The numbers behind UNITD OS-49
          </h2>
 
          <div className="mt-[30px]">
  <p className="text-[16px] leading-[24px] mb-[30px] text-[#171717] mb-[50px]">
    ₹128 Crore MOU - chinnavedampatı, Saravanampatti, Coimbatore
  </p>

  <div className="grid grid-cols-2 2xl:grid-cols-4 justify-between gap-x-[35px] 2xl:gap-[35px] items-start relative">
    <div className="py-[30px] md:py-[50px] border-t group">
      <div>
        <h3 className="text-[22px] leading-[28px] md:text-[24px] md:leading-[30px] lg:text-[26px] lg:leading-[34px] 2xl:text-[28px] 2xl:leading-[36px] text-[#b08d38] tracking-[-0.04em] mb-[20px] font-[400] group-hover:text-[#b08d38] transition-colors duration-300">
          ₹128 crore
        </h3>

        <p className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
          Total investment
        </p>
      </div>
    </div>

    <div className="py-[30px] md:py-[50px] border-t group">
      <div>
        <h3 className="text-[22px] leading-[28px] md:text-[24px] md:leading-[30px] lg:text-[26px] lg:leading-[34px] 2xl:text-[28px] 2xl:leading-[36px] text-[#b08d38] tracking-[-0.04em] mb-[20px] font-[400] group-hover:text-[#b08d38] transition-colors duration-300">
          2,000+
        </h3>

        <p className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
          Workplaces planned
        </p>
      </div>
    </div>

    <div className="py-[30px] md:py-[50px] border-t group">
      <div>
        <h3 className="text-[22px] leading-[28px] md:text-[24px] md:leading-[30px] lg:text-[26px] lg:leading-[34px] 2xl:text-[28px] 2xl:leading-[36px] text-[#b08d38] tracking-[-0.04em] mb-[20px] font-[400] group-hover:text-[#b08d38] transition-colors duration-300">
          2.2 lakh sq.ft.
        </h3>

        <p className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
          Commercial space planned
        </p>
      </div>
    </div>

    <div className="py-[30px] md:py-[50px] border-t group">
      <div>
        <h3 className="text-[22px] leading-[28px] md:text-[24px] md:leading-[30px] lg:text-[26px] lg:leading-[34px] 2xl:text-[28px] 2xl:leading-[36px] text-[#b08d38] tracking-[-0.04em] mb-[20px] font-[400] group-hover:text-[#b08d38] transition-colors duration-300">
          1
        </h3>

        <p className="text-[16px]/[24px] md:text-[18px]/[26px] text-[#000] font-[400] tracking-[-0.03em]">
          Dedicated incubation centre
        </p>
      </div>
    </div>
  </div>
</div>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            UNITD OS is positioned as The Operating System for Tomorrow’s Enterprise, a workspace for tech companies, startups, and SMEs. Its infrastructure is designed to grow alongside the Saravanmapatti IT corridor.
          </p>

          <h2 className="text-[20px] leading-[28px] 2xl:text-[22px] 2xl:leading-[30px] font-medium text-gray-900 mb-[20px] mt-[35px]">
            Strong Momentum Across UNITD’s Residential Portfolio
          </h2>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            Alongside its expansion into IT commercial infrastructure, UNITD continues to make steady progress across its residential brands - <strong>MUH</strong> for apartments and <Link
              href="https://www.pluswilla.com/" 
              rel="noopener noreferrer"
              className="font-medium underline"
            >
              <strong>+Willa</strong>
            </Link> for villas.
          </p>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            Under the <strong>MUH</strong> brand, the company’s first major development, <strong>MUH UNITD 49</strong>, comprising <strong>190 apartments</strong>, has reached the handover stage. Apartment handovers have commenced <strong> on schedule, in line with the commitment made to customers </strong>, reinforcing UNITD’s emphasis on timely project delivery.
          </p>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            Building on the success of U49, UNITD launched <strong>MUH 49.2</strong> at Saravanampatti in <strong>May 2026</strong>. The residential community comprises <strong>196 apartments</strong>, and construction has commenced. The project is positioned to address the growing demand for
            thoughtfully planned and accessible urban housing in one of Coimbatore’s fastest-developing neighbourhoods.
          </p>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            The company is also preparing for its next residential development, MUH 04, at Peelamedu on Avinashi Road, further expanding MUH's presence across key residential corridors of Coimbatore. <Link
              href="http://muh.co.in"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline"
            >
              <strong>muh.co.in</strong>
            </Link>
          </p>

          <h2 className="text-[20px] leading-[28px] 2xl:text-[22px] 2xl:leading-[30px] font-medium text-gray-900 mb-[20px] mt-[35px]">
            Building the Next Phase of UNITD
          </h2>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            The <strong> ₹128 crore UNITD - OS 49 project at Chinnavedampatti </strong> represents UNITD’s entry into a new phase of growth and reflects the company’s commitment to contributing to <strong> Coimbatore’s emergence as a major workspace and investment destination</strong>.
          </p>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            The company expressed its gratitude to the <strong> Government of Tamil Nadu and the Hon’ble Chief Minister
            </strong> for their encouragement and <strong>support for investments</strong> that strengthen the State’s
            technology and employment ecosystem.
          </p>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            With <strong>MUH U49 entering handover,</strong> <Link
              href="https://www.muh.co.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline"
            >
              <strong>MUH 49.2</strong>
            </Link> - the second phase of MUH 49 under construction, +willa 25 - community villas progressing towards completion, MUH 04 - residential projects preparing to enter the Peelamedu-Avinashi Road, and now UNITD OS-49 - the commercial development taking shape at Saravanampatti, UNITD Living Space continues its journey of creating developments that respond to the evolving aspirations of Coimbatore. The company will soon <strong>expand into other cities</strong>.
          </p>

          <h2 className="text-[20px] leading-[28px] 2xl:text-[22px] 2xl:leading-[30px] font-medium text-gray-900 mb-[20px] mt-[35px]">
            Strategic location of UNITD OS
          </h2>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            UNITD OS-49 is coming up in Chinnavedampatti, Saravanampatti, Coimbatore - 641 049 right inside Coimbatore&apos;s IT corridor, close to KCT Tech Park, KGISL Technologies, and CHIL SEZ. It&apos;s not a standalone office building on the edge of the map; it&apos;s landing directly in a location that&apos;s already an established tech and employment hub.
          </p>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            The development is designed to accommodate 2,000+ workplaces once fully operational. That&apos;s a significant addition of people working right in this corridor - startups, SMEs, consultants, and
            enterprises all bringing employees into the same few square kilometres where MUH 49.2 is being built.
          </p>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            More workplaces in the corridor means more people who need to live within a short commute of where they work. That&apos;s more housing demand landing directly on Saravanampatti&apos;s doorstep.
          </p>
        </>
      ),

      content2: (
        <>
          <h2 className="text-[20px] leading-[28px] 2xl:text-[22px] 2xl:leading-[30px] font-medium text-gray-900 mb-[20px] mt-[35px]"> UNITD&apos;s direction: &quot;Keep Solving for Better Living&quot; </h2>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            This MoU is part of a broader expansion. <Link
              href="https://www.liveunitd.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline"
            >
              UNITD Living
            </Link> Space now operates across three fronts - apartments under MUH, villas under +Willa, and commercial real estate under UNITD OS - with plans to expand into other cities of Tamil Nadu and beyond. The company frames all of it under one philosophy: Keep Solving for Better Living. 
          </p>

          <p className="text-[16px] leading-[24px] mb-[15px] text-[#171717]">
            <strong>Want to see the numbers behind MUH 49.2 directly?</strong>
          </p>

          <p className="text-[16px] leading-[24px] mb-[24px] text-[#171717]">
            <Link
              href="https://www.muh.co.in/muh-u49-2"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium underline"
            >
              Explore MUH 49.2 floor plans and pricing
            </Link>
          </p>
        </>
      ),
    },
  ];

  return (
    <div className="sticky top-[130px] z-[2]">
      {data.map((item, index) => (
        <div className="group relative" key={index}>
          <h1 className="text-[28px]/[36px] md:text-[32px]/[40px] lg:text-[36px]/[44px] xl:text-[40px]/[48px] 2xl:text-[48px]/[54px] tracking-[-0.03em] font-[500] text-[var(--secondary)]">
            {item.title}
          </h1>

          <div className="rounded-[10px] overflow-hidden mt-[50px] max-mlg:mt-[30px]">
            <Image
              src={`${ASSET_PREFIX}${item.image}`}
              width={530}
              height={500}
              alt="UNITD Living Space Signs ₹128 Crore MoU for a New IT Project in Saravanampatti"
              unoptimized
              className="w-full max-h-[500px] object-cover group-hover:scale-[1.1] transition duration-300 ease-linear"
            />
          </div>

          <div className="mt-[50px]">
            <p className="my-[20px] text-[20px] leading-[28px] font-[400] max-xxl:text-[18px] max-xxl:leading-[26px] group-hover:text-[#8e722d] transition-colors duration-300 max-mlg:text-[16px] max-mlg:leading-[24px] max-xl:my-[15px]">
              {item.title}
            </p>

            <div className="text-[16px] leading-[24px] mb-[20px] w-full font-light">
              {item.content1}
            </div>
          </div>

          <h2 className="text-[20px] leading-[28px] 2xl:text-[22px] 2xl:leading-[30px] font-medium text-gray-900 mb-[15px] mt-[35px]">
            Frequently asked questions
          </h2>

          <div className="mb-[20px]">
            <AccordionDemo />
          </div>

          <div className="text-[16px] leading-[24px] mb-[20px] w-full font-light">
            {item.content2}
          </div>
        </div>
      ))}
    </div>
  );
}